import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const ServicesSection = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const services = [
    {
      icon: 'ri-truck-line',
      title: 'Crane Rental',
      description: 'Diverse fleet of cranes with various sizes and capacities to meet all your construction project needs',
      gradient: 'from-accent to-accent-dark',
      textColor: 'text-white',
    },
    {
      icon: 'ri-building-line',
      title: 'General Contracting',
      description: 'Executing construction projects with the highest quality and professionalism standards with a qualified and trained team',
      gradient: 'from-white to-white',
      textColor: 'text-primary',
      border: true,
    },
    {
      icon: 'ri-loader-line',
      title: 'Heavy Transport',
      description: 'Lifting and transporting heavy equipment and materials with the latest technologies and highest safety standards',
      gradient: 'from-primary to-primary-light',
      textColor: 'text-white',
    },
    {
      icon: 'ri-hammer-line',
      title: 'Construction Projects',
      description: 'Managing and executing major construction projects from planning to final delivery',
      gradient: 'from-white to-white',
      textColor: 'text-primary',
      border: true,
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Major Project Support',
      description: 'Providing technical and consulting support for major projects with over 15 years of experience',
      gradient: 'from-accent to-accent-dark',
      textColor: 'text-white',
    },
  ];

  return (
    <section id="services" className="py-24 bg-white">
      <div className="container mx-auto px-4 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-accent rounded-full"></div>
            <span className="text-accent font-poppins font-semibold text-sm">What We Offer</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-inter font-black text-primary mb-6">
            Our Comprehensive Services
          </h2>
          <p className="text-neutral-500 font-poppins text-xl max-w-2xl mx-auto">
            Complete solutions for all your construction needs with the highest quality and safety standards
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`bg-gradient-to-br ${service.gradient} rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 group cursor-pointer ${
                service.border ? 'border-2 border-neutral-200' : ''
              }`}
            >
              {/* Icon */}
              <motion.div 
                className={`w-16 h-16 ${service.textColor === 'text-white' ? 'bg-white/20' : 'bg-accent/10'} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300`}
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <i className={`${service.icon} text-4xl ${service.textColor === 'text-white' ? 'text-white' : 'text-accent'}`}></i>
              </motion.div>

              <h3 className={`text-2xl font-inter font-bold ${service.textColor} mb-4`}>
                {service.title}
              </h3>
              
              <p className={`${service.textColor === 'text-white' ? 'text-white/80' : 'text-neutral-600'} font-poppins text-base leading-relaxed`}>
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
